/**
 * Drop-in replacements for the `lucide-react` API, backed by the Coolicons
 * webfont (442 glyphs — see `src/coolicons.css`). Every icon the app uses
 * is re-exported from here under its original lucide name so call sites
 * don't have to change JSX, only the import path:
 *
 *   // was
 *   import { Wallet, Loader2 } from "lucide-react";
 *   // now
 *   import { Wallet, Loader2 } from "@/components/ui/icons";
 *
 * When a lucide icon has no direct Coolicon equivalent we pick the closest
 * glyph and note it here. If a substitute reads wrong in-context, swap to
 * a different Coolicon at the call site using `<CoolIcon name="..." />`.
 *
 * Animation affordance: `Loader2` keeps lucide's `animate-spin` contract so
 * existing `<Loader2 className="animate-spin" />` markup still renders.
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { CoolIcon } from "@/components/ui/cool-icon";
import { cn } from "@/lib/utils";

/**
 * Icon props mirror the shape lucide-react exposes, so call sites don't
 * have to change JSX. SVG-only props (strokeWidth, fill, stroke) are
 * accepted for API-compat and silently dropped — Coolicons is a webfont
 * so stroke width is fixed by the glyph itself.
 */
export type IconProps = Omit<ComponentPropsWithoutRef<"span">, "children"> & {
  size?: number | string;
  strokeWidth?: number | string;
  fill?: string;
  stroke?: string;
  absoluteStrokeWidth?: boolean;
};

/**
 * Tailwind spacing scale → rem, for translating lucide-style sizing
 * classes (`h-4 w-4`, `size-5`) into an explicit pixel size on the
 * underlying `<CoolIcon>`. Without this the glyph inherits its parent
 * `font-size` — which makes every migrated `<Icon className="h-4 w-4" />`
 * call site render at body-text size, defeating the caller's intent.
 *
 * Values come from Tailwind's default spacing scale (1 unit = 0.25rem).
 * If a caller uses an off-scale value (e.g. `h-[18px]`) we skip the
 * translation and fall back to `1em`.
 */
const TW_SIZE_CLASS = /(?:^|\s)(?:size|[hw])-(\d+(?:\.\d+)?)(?:\s|$)/;

function resolveSize(
  explicit: number | string | undefined,
  className: string | undefined,
): number | string | undefined {
  if (explicit !== undefined) return explicit;
  if (!className) return undefined;
  const m = className.match(TW_SIZE_CLASS);
  if (!m || !m[1]) return undefined;
  const units = Number(m[1]);
  if (!Number.isFinite(units)) return undefined;
  return `${units * 0.25}rem`;
}

function make(glyph: string, extraClassName?: string) {
  return function Icon({
    className,
    size,
    // SVG-only props we accept for lucide compat and drop.
    strokeWidth: _strokeWidth,
    fill: _fill,
    stroke: _stroke,
    absoluteStrokeWidth: _absoluteStrokeWidth,
    ...rest
  }: IconProps): ReactNode {
    const resolved = resolveSize(size, className);
    return (
      <CoolIcon
        name={glyph}
        size={resolved}
        className={cn(extraClassName, className)}
        {...rest}
      />
    );
  };
}

// Arrows
export const ArrowLeft = make("Arrow_Left_MD");
export const ArrowRight = make("Arrow_Right_MD");
export const ArrowLeftRight = make("Arrow_Left_Right");
export const ArrowUpRight = make("Arrow_Up_Right_MD");
export const ArrowDownRight = make("Arrow_Down_Right_MD");
export const ChevronDown = make("Chevron_Down");
export const ChevronUp = make("Chevron_Up");
export const ChevronLeft = make("Chevron_Left");
export const ChevronRight = make("Chevron_Right");
export const ChevronsUpDown = make("Arrow_Down_Up");

// Aliased `*Icon` variants shadcn uses internally
export const ChevronDownIcon = ChevronDown;
export const ChevronUpIcon = ChevronUp;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;

// Status / shields
export const Shield = make("Shield");
export const ShieldCheck = make("Shield_Check");
export const ShieldAlert = make("Shield_Warning");
export const AlertTriangle = make("Triangle_Warning");
export const TriangleAlertIcon = AlertTriangle;
export const OctagonXIcon = make("Octagon_Warning");
export const CheckCircle2 = make("Circle_Check");
export const CircleCheckIcon = CheckCircle2;
export const XCircle = make("Close_Circle");
export const Circle = make("Circle");

// Forms / actions
export const Check = make("Check");
export const CheckIcon = Check;
export const X = make("Close_LG");
export const XIcon = X;
export const Plus = make("Add_Plus");
export const Search = make("Search_Magnifying_Glass");
export const SearchX = make("Search_Magnifying_Glass"); // no "no-results" variant; consumer usually swaps for a Close glyph alongside
export const Filter = make("Filter");
export const Pencil = make("Edit_Pencil_02");
export const Download = make("Download");
export const Copy = make("Copy");
export const Save = make("Save");
export const RotateCw = make("Arrow_Reload_02");
export const MoreHorizontal = make("More_Horizontal");
export const MoreHorizontalIcon = MoreHorizontal;
export const Link2 = make("Link");
export const ExternalLink = make("External_Link");

// Spinner — keep lucide's `animate-spin` contract. `Loading` glyph is
// already a ring; class can add `animate-spin` for the same effect.
export const Loader2 = make("Loading", "");
export const Loader2Icon = Loader2;

// Info / help
export const Info = make("Info");
export const InfoIcon = Info;
export const HelpCircle = make("Circle_Help");

// Nav / chrome
export const Bell = make("Bell");
export const Settings = make("Settings_Future");
export const LogOut = make("Log_Out");
export const User = make("User_Circle");
export const Users = make("Users");
export const PanelLeftIcon = make("Window_Sidebar");
export const Menu = make("Menu_Alt_01");
export const Home = make("House_01");

// Theme
export const Sun = make("Sun");
export const Moon = make("Moon");

// Time
export const Calendar = make("Calendar");
export const Clock = make("Clock");

// Comms
export const Mail = make("Mail");
export const MessageSquare = make("Chat");
export const Send = make("Paper_Plane");

// Domain — closest Coolicon match where no 1:1 glyph exists.
// If any of these read wrong in UI, replace at the call site with a
// <CoolIcon name="..." /> that fits better.
export const Wallet = make("Credit_Card_02"); // closest to wallet / balance
export const Banknote = make("Credit_Card_01");
export const DollarSign = make("Credit_Card_01");
export const Brain = make("Bulb"); // "thinking"
export const Sparkles = make("Star");
export const Award = make("Star");
export const Trophy = make("Star");
export const Target = make("Map_Pin");
export const Activity = make("Chart_Line");
export const BarChart3 = make("Chart_Bar_Vertical_01");
export const TrendingUp = make("Trending_Up");
export const TrendingDown = make("Trending_Down");
export const Building2 = make("Building_02");
export const Newspaper = make("File_Document");
export const Sprout = make("Leaf");
export const Flame = make("Triangle_Warning"); // "hot" signal
export const Zap = make("Bulb"); // "flash of action"
export const KeyRound = make("Keyboard");
export const Code2 = make("Code");
export const Cpu = make("Main_Component");
export const Ban = make("Close_Circle");
export const Pause = make("Pause");
export const Play = make("Play");
export const LayoutGrid = make("More_Grid_Big");
export const Server = make("Desktop_Tower");
export const Monitor = make("Window_Terminal");
export const Image = make("Image_02");

// Type alias matching `lucide-react`'s `LucideIcon` — some components type
// an icon prop as `LucideIcon`; re-exporting the same name keeps those
// annotations compiling without a refactor.
export type LucideIcon = (props: IconProps) => ReactNode;
